/** Fallback when the backend did not provide a usable landing URL. */
const ROOT_URL = '/'

/**
 * Matches a landing URL that points to a specific network root:
 * `/clients/{clientId}/network/{networkId}` (no sub-path, no trailing slash).
 *
 * The IDs are matched as any non-slash sequence — we trust the backend to
 * produce valid IDs (this util is the last step before navigation).
 */
const PROJECT_LANDING_REGEX = /^\/clients\/[^/]+\/network\/[^/]+$/

/**
 * Resolves the post-login destination from the backend's landing URL and
 * the current viewport.
 *
 * Rationale: the backend `landingOrchestrator` decides the *business*
 * destination (which client / network / project) but is intentionally
 * agnostic of the viewport. The viewport is a pure presentation concern
 * that only the client can determine reliably, so we adapt the URL here.
 *
 * Behavior:
 *   - Missing URL (`null` / `undefined` / empty string) → fall back to the
 *     app root. The backend's contract types this as `string`, but we
 *     defend against a broken response so callers don't have to.
 *   - Desktop (or unknown viewport) → URL returned unchanged.
 *   - Mobile + URL points to a project root → append `/activities` so the
 *     user lands directly on the activity table view (the only view that
 *     makes sense on a small screen for now).
 *   - Mobile + URL is anything else (root, client root, or already a
 *     sub-path) → URL returned unchanged. We trust the backend when it
 *     gave us a more specific path, and we cannot guess a project for
 *     client-root or app-root URLs.
 *
 * @param landingUrl URL as returned by the backend (`data.landingUrl`).
 *                   Nullable to absorb a broken backend response.
 * @param isMobile Whether the current viewport is mobile-sized.
 * @returns The URL to actually navigate to (always a non-empty string).
 */
export const resolveLandingUrl = (
  landingUrl: string | null | undefined,
  isMobile: boolean,
): string => {
  if (!landingUrl) return ROOT_URL
  if (!isMobile) return landingUrl
  if (!PROJECT_LANDING_REGEX.test(landingUrl)) return landingUrl
  return `${landingUrl}/activities`
}
